// Helper functions outside the Zustand store
import { useAppMessageStore } from '../stores';

import type { AppMessage, WebMessage } from '../types';

const APP_HANDLER = 'EpytMessageHandler';
const AOS_INTERFACE_FUNCTION = 'receiveWebMessage';

let isListenerInitialized = false;

export const initializeMessageListener = () => {
    if (isListenerInitialized) {
        return;
    }
    const handleMessage = (event: MessageEvent) => {
        try {
            const message: AppMessage = JSON.parse(event.data);
            useAppMessageStore.getState().handleMessage(message);
        } catch (error) {
            console.error('Error processing message:', error);
        }
    };

    window.addEventListener('AppMessage', handleMessage);
    isListenerInitialized = true;

    return () => {
        window.removeEventListener('AppMessage', handleMessage);
        isListenerInitialized = false;
    };
};

/**
 * Centralized mobile app detection utility
 * @returns Object with mobile app detection flags
 */
export const getMobileAppInfo = () => {
    const application = window['EPYT_APP_APPLICATION'];
    const platform = window['EPYT_APP_PLATFORM'];

    const isOnMobileApp = application?.toLowerCase() === 'epyt';
    const isIOS = platform === 'ios';
    const isAOS = platform === 'aos' || platform === 'android';

    return { isOnMobileApp, isIOS, isAOS };
};

export const sendWebMessage = (message: WebMessage) => {
    const { isOnMobileApp, isIOS, isAOS } = getMobileAppInfo();

    if (!isOnMobileApp) {
        console.log('sendMessage: ', message);
    }

    if (isIOS) {
        sendIosMessage(message);
        return;
    }

    if (isAOS) {
        sendAosMessage(message);
        return;
    }
};

export const sendIosMessage = (message: WebMessage) => {
    const hasNotMessageHandler = !(
        window['webkit'] &&
        window['webkit'].messageHandlers &&
        window['webkit'].messageHandlers[APP_HANDLER]
    );
    if (hasNotMessageHandler) {
        return;
    }

    const messageHandler = window['webkit'].messageHandlers[APP_HANDLER];
    messageHandler.postMessage(JSON.stringify(message));
};

export const sendAosMessage = (message: WebMessage) => {
    const messageHandler = window[APP_HANDLER];
    if (!messageHandler) {
        return;
    }

    const hasNotInterface =
        !messageHandler[AOS_INTERFACE_FUNCTION] || typeof messageHandler[AOS_INTERFACE_FUNCTION] !== 'function';
    if (hasNotInterface) {
        return;
    }

    messageHandler[AOS_INTERFACE_FUNCTION](JSON.stringify(message));
};

export const getIsWebView = () => {
    const { isOnMobileApp, isIOS, isAOS } = getMobileAppInfo();
    return { isWebView: isOnMobileApp, isIOS, isAOS };
};
